import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from './AppIcon';

const PdfDocumentViewer = lazy(() => import('./PdfDocumentViewer'));

const MASTER_PDF_URL = '/documents/maestro-europa-2026.pdf';
const MASTER_CACHE = 'europa-2026-master-document-v3';

type MasterState = 'online' | 'cached' | 'unavailable';

async function getCachedMaster() {
  if (!('caches' in window)) return null;
  const cache = await caches.open(MASTER_CACHE);
  return cache.match(MASTER_PDF_URL);
}

async function cacheMasterInBackground() {
  if (!navigator.onLine || !('caches' in window)) return;
  try {
    const response = await fetch(MASTER_PDF_URL, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/pdf' },
    });
    if (!response.ok) return;
    const cache = await caches.open(MASTER_CACHE);
    await cache.put(MASTER_PDF_URL, response.clone());
  } catch {
    // Opening the PDF must never block the WebApp.
  }
}

function stateLabel(state: MasterState) {
  switch (state) {
    case 'online': return 'PDF consolidado · última versión';
    case 'cached': return 'Última copia disponible offline';
    default: return 'Conéctate una vez para preparar el PDF';
  }
}

export default function MasterDocumentEnhancement() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<MasterState>(navigator.onLine ? 'online' : 'unavailable');
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const entryButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let lastHost: HTMLElement | null = null;
    let scheduled = false;

    const locate = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const nextHost = document.querySelector<HTMLElement>('.more-grid');
        if (nextHost !== lastHost) {
          lastHost = nextHost;
          setHost(nextHost);
        }
      });
    };

    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;

    const updateConnectivity = async () => {
      if (navigator.onLine) {
        if (active) setState('online');
        return;
      }
      const cached = await getCachedMaster();
      if (active) setState(cached ? 'cached' : 'unavailable');
    };

    void updateConnectivity();
    const onOnline = () => { if (active) setState('online'); };
    const onOffline = () => { void updateConnectivity(); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      active = false;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!viewerUrl) return;
    const appShell = document.querySelector<HTMLElement>('.app-shell');
    const wasInert = appShell?.hasAttribute('inert') ?? false;
    document.body.classList.add('master-viewer-open');
    appShell?.setAttribute('inert', '');
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMaster();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('master-viewer-open');
      if (!wasInert) appShell?.removeAttribute('inert');
      window.removeEventListener('keydown', onKeyDown);
      entryButtonRef.current?.focus();
    };
  }, [viewerUrl]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const subtitle = useMemo(() => stateLabel(state), [state]);

  function closeMaster() {
    setViewerUrl(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }

  async function openMaster() {
    if (navigator.onLine) {
      setViewerUrl(MASTER_PDF_URL);
      void cacheMasterInBackground();
      return;
    }

    const cached = await getCachedMaster();
    if (!cached) {
      setState('unavailable');
      return;
    }

    const blob = await cached.blob();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(blob);
    setViewerUrl(objectUrlRef.current);
  }

  if (!host && !viewerUrl) return null;

  return (
    <>
      {host && createPortal(
        <button ref={entryButtonRef} type="button" className="master-document-entry" onClick={openMaster}>
          <span aria-hidden="true"><AppIcon name="master" /></span>
          <div><strong>Documento maestro</strong><small>{subtitle}</small></div>
          <b><AppIcon name="chevronRight" size={20} /></b>
        </button>,
        host,
      )}
      {viewerUrl && createPortal(
        <section className="master-document-viewer" role="dialog" aria-modal="true" aria-label="Documento maestro">
          <header className="master-viewer-toolbar">
            <button ref={closeButtonRef} type="button" onClick={closeMaster} aria-label="Volver a Europa">
              <AppIcon name="chevronLeft" size={20} />
              <span>Volver a Europa</span>
            </button>
            <div><strong>Documento maestro</strong><small>Revisión 4</small></div>
          </header>
          <Suspense fallback={<div className="deferred-module-status" role="status">Preparando visor PDF…</div>}>
            <PdfDocumentViewer src={viewerUrl} title="Documento maestro Europa 2026, Revisión 4" />
          </Suspense>
        </section>,
        document.body,
      )}
    </>
  );
}
